import React from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { useMediaStudio } from '../../context/MediaStudioContext';
import { isAdditiveSelectEvent } from '../../utils/isAdditiveSelectEvent';
import AssetsState from '../../components/assets-state/AssetsState';
import PanelBody from '../../components/panel-body/PanelBody';
import PanelSection from '../../components/panel-section/PanelSection';
import LayerItem from './components/layer-item/LayerItem';

import scss from './LayersPanel.module.scss';

function LayersPanel() {
  const { t } = useTranslation('mediaStudio');
  const { editor, features } = useMediaStudio();
  const { elements, selectedIds } = editor;
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const displayOrder = [...elements].reverse();
  const sortableIds = displayOrder.map((element) => element.id);

  const handleSelect = (id, event) => {
    if (features.multiSelect && isAdditiveSelectEvent(event)) {
      editor.toggleSelection(id);
      return;
    }
    editor.selectElement(id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldDisplayIndex = displayOrder.findIndex((el) => el.id === active.id);
    const newDisplayIndex = displayOrder.findIndex((el) => el.id === over.id);
    if (oldDisplayIndex === -1 || newDisplayIndex === -1) {
      return;
    }

    const nextDisplay = arrayMove(displayOrder, oldDisplayIndex, newDisplayIndex);
    editor.reorderElements([...nextDisplay].reverse());
  };

  return (
    <PanelBody>
      <PanelSection title={t('layers')}>
        <AssetsState isEmpty={!elements.length} emptyText={t('layersEmpty')}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <div className={scss.list}>
                {displayOrder.map((element) => (
                  <LayerItem
                    key={element.id}
                    element={element}
                    isSelected={selectedIds.includes(element.id)}
                    onSelect={handleSelect}
                    onToggleVisibility={editor.toggleVisibility}
                    onDelete={editor.deleteElement}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </AssetsState>
      </PanelSection>
    </PanelBody>
  );
}

export default LayersPanel;
